/*
  MASTER — motor de oferta por cantidad.

  Una sola regla para calcular lo que vale un nivel del selector de packs, usada
  por el render de Liquid (snippets/master-offer-tier.liquid) y por el recálculo
  al cambiar de variante (assets/master-product.js). Cuando la misma aritmética
  vivía en dos sitios acabábamos con dos respuestas distintas; el bug del formato
  de dinero de la sesión 4 salió exactamente de ahí.

  Contrato de honestidad
  ----------------------
  El theme NO fabrica ahorro. `source` declara de dónde sale la ventaja
  económica y es Shopify quien la entrega:

    none      -> no hay ventaja por cantidad. El nivel vale cantidad × precio y
                 no se pinta ni tachado ni cifra de ahorro, aunque el merchant
                 haya dejado valores configurados. Es el default.
    automatic -> existe una promoción automática de Shopify (o de una app) que
                 aplica sola al llegar al carrito.
    codes     -> existe un código de Shopify por nivel, que el theme aplica al
                 añadir y vuelve a leer del carrito real para confirmarlo.

  En `automatic` y `codes` la cifra que se muestra antes de añadir al carrito es
  una PROYECCIÓN de lo declarado por el merchant: Shopify no puede cotizar un
  carrito hipotético. Por eso `verify()` compara la proyección contra el carrito
  real después de añadir, y quien manda es el carrito.

  Todo se calcula en la unidad mínima de la divisa (lo que Shopify llama cents) y
  se formatea con MasterMoney. Aquí no se convierte moneda ni se asume ninguna.
*/
(() => {
  const SOURCES = ['none', 'automatic', 'codes'];
  const TYPES = ['none', 'amount', 'percent'];

  const num = value => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  /* Redondeo explícito en los dos lados: Liquid trunca en `divided_by` con
     enteros, así que sin un `round` declarado el servidor y el cliente se
     separaban un céntimo en los porcentajes. */
  const advantageFor = (reference, saveType, saveValue) => {
    const value = num(saveValue);
    if (value <= 0) return 0;
    if (saveType === 'amount') return Math.round(value * 100);
    if (saveType === 'percent') return Math.round(reference * value / 100);
    return 0;
  };

  /* Precio por unidad. Dividir en cents deja restos que la divisa no sabe
     escribir: 209.900 COP entre 3 daban 6.996.666,67 cents, que el formateador
     pinta como «$69.966,67 c/u». En pesos colombianos los centavos no existen,
     así que eso se lee como un error del sistema justo en el buy box.

     La regla se autodetecta y no asume ninguna divisa: si el precio del nivel
     es un número redondo de unidades mayores (resto 0 al dividir entre 100), la
     tienda no está usando la unidad menor y el precio por unidad se ajusta a la
     unidad mayor más cercana. Si el nivel sí tiene céntimos (19,99 USD), el
     resto no es 0 y la aritmética exacta se respeta tal cual.

     Sólo afecta a lo que se MUESTRA por unidad. `price` y `advantage` —lo que
     Shopify va a cobrar y descontar— no se tocan. */
  const unitPriceFor = (price, quantity) => {
    const qty = Math.max(1, Math.round(num(quantity) || 1));
    const exact = Math.round(num(price) / qty);
    return num(price) % 100 === 0 ? Math.round(exact / 100) * 100 : exact;
  };

  /*
    unitPrice : precio de una unidad en cents (variant.price)
    qty       : unidades del nivel
    source    : none | automatic | codes
    saveType  : none | amount | percent
    saveValue : importe en unidad mayor de la divisa (9900 = 9.900) o porcentaje
  */
  const resolveTier = ({unitPrice, qty, source, saveType, saveValue} = {}) => {
    const unit = Math.max(0, Math.round(num(unitPrice)));
    const quantity = Math.max(1, Math.round(num(qty) || 1));
    const src = SOURCES.includes(source) ? source : 'none';
    const type = TYPES.includes(saveType) ? saveType : 'none';
    const reference = unit * quantity;

    // La puerta: sin fuente real declarada no hay ventaja que mostrar, por muy
    // configurado que esté el nivel. Esto es lo que impide que el selector
    // parezca descontado sin que Shopify vaya a cobrar menos.
    const raw = src === 'none' ? 0 : advantageFor(reference, type, saveValue);
    const advantage = Math.min(Math.max(0, raw), reference);
    const price = reference - advantage;

    return {
      source: src,
      quantity,
      reference,                                   // cantidad × precio, sin ventaja
      advantage,                                   // lo que Shopify debe descontar
      price,                                       // lo que debería cobrarse
      unitPrice: unitPriceFor(price, quantity),    // precio por unidad resultante
      // Sólo hay algo que comparar si hay ventaja: si no, no se pinta tachado.
      hasAdvantage: advantage > 0
    };
  };

  /*
    Reconciliación contra el carrito real de Shopify. Devuelve lo que de verdad
    pasó, no lo que esperábamos. `expected` es el resultado de resolveTier().
  */
  const verify = (expected, cart, productId) => {
    if (!expected || !cart || !Array.isArray(cart.items)) return null;
    const pid = Number(productId || 0);
    const items = cart.items.filter(item => !pid || Number(item.product_id) === pid);
    if (!items.length) return null;

    const original = items.reduce((sum, item) => sum + num(item.original_line_price ?? num(item.original_price) * num(item.quantity)), 0);
    const finalTotal = items.reduce((sum, item) => sum + num(item.final_line_price ?? num(item.final_price) * num(item.quantity)), 0);
    const actualAdvantage = Math.max(0, original - finalTotal);

    return {
      expectedAdvantage: expected.advantage,
      actualAdvantage,
      actualTotal: finalTotal,
      // Shopify entregó al menos lo prometido. Menos que eso es una promesa rota
      // y quien la resuelve es el merchant en Shopify → Descuentos, no el theme.
      honoured: actualAdvantage + 1 >= expected.advantage
    };
  };

  window.MasterOffer = {SOURCES, TYPES, resolveTier, verify, unitPriceFor};
})();
